/* eslint-disable no-restricted-globals */
import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AsyncSelect from "react-select/async";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { Loading } from "../../../components/Loading/Loading";
import { RootState } from "../../../store/store";
import { API } from "../../../config/api";
Modal.setAppElement("#root");

const locationOptions = [
  { value: "JTP1", label: "Jatim Park 1" },
  { value: "JTP2", label: "Jatim Park 2" },
  { value: "JTP3", label: "Jatim Park 3" },
  { value: "ECO", label: "Eco Green Park" },
  { value: "SENYUM", label: "Senyum World Hotel" },
  { value: "MGG", label: "Milenial Glow Garden" },
  { value: "PFP", label: "Predator Fun Park" },
  { value: "POHONINN", label: "Pohon Inn Hotel" },
  { value: "MA", label: "Museum Angkut" },
  { value: "BALOGA", label: "Batu Love Garden" },
];

type IItem = {
  product_id: any;
  type: string;
  qty: number;
};

type IInputsStockAdjustment = {
  branch_id: string;
  reason: string;
  items: IItem[];
};

export default function StockAdjustment() {
  const globalStateUser: any = useSelector((state: RootState) => {
    return state.userData;
  });
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalData, setTotalData] = useState({ from: "", to: "", total: "" });
  const [pageNav, setPageNav] = useState({ nextPage: "", preffPage: "" });
  const [editId, setEditId] = useState<string | null>(null);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm<IInputsStockAdjustment>({
    defaultValues: {
      items: [{ product_id: "", type: "OUT", qty: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  } as never);

  useEffect(() => {
    if (globalStateUser.user?.role_id === "accounting") {
      navigate("/transaksi");
    }
    setIsLoading(true);
    axios
      .get(
        (API.STOCK_ADJUSTMENT) + `?page=${page}`,
        {
          headers: { Authorization: `Bearer ${globalStateUser.token}` },
        }
      )
      .then((res) => {
        setData(res.data.data);
        setTotalData({
          from: res.data.from,
          to: res.data.to,
          total: res.data.total,
        });
        setPageNav({
          nextPage: res.data.next_page_url,
          preffPage: res.data.prev_page_url,
        });
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [page]);

  function toggleModal(rowData?: any) {
    if (!isOpenModal && rowData?.id) {
      // Edit mode: fetch detail then pre-fill
      setEditId(rowData.id);
      axios
        .get(
          (API.STOCK_ADJUSTMENT) + `/${rowData.id}`,
          {
            headers: { Authorization: `Bearer ${globalStateUser.token}` },
          }
        )
        .then((res) => {
          const detail = res.data.data;
          reset({
            branch_id: detail.branch_id,
            reason: detail.reason,
            items: detail.items.map((item: any) => ({
              product_id: {
                label: item.product.name,
                value: item.product_id,
              },
              type: item.type,
              qty: item.qty,
            })),
          });
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      // Add mode or close
      setEditId(null);
      reset({
        branch_id: "",
        reason: "",
        items: [{ product_id: "", type: "OUT", qty: 0 }],
      });
    }
    setIsOpenModal(!isOpenModal);
  }

  const handleDetail = (id: string) => {
    axios
      .get(
        (API.STOCK_ADJUSTMENT) + `/${id}`,
        {
          headers: { Authorization: `Bearer ${globalStateUser.token}` },
        }
      )
      .then((res) => {
        const d = res.data.data;
        Swal.fire({
          title: "Detail Stock Adjustment",
          width: "auto",
          html: `
            <style>
              .sa-tg { border-collapse: collapse; width: 100%; margin-top: 12px; }
              .sa-tg th { background-color: #409cff; color: #fff; padding: 8px 12px; }
              .sa-tg td { background-color: #EBF5FF; padding: 8px 12px; border-bottom: 1px solid #9ABAD9; }
              .badge-in  { background: #22c55e; color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 12px; }
              .badge-out { background: #ef4444; color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 12px; }
              .sa-info { text-align: left; margin-bottom: 4px; }
            </style>
            <div class="sa-info"><b>Code:</b> ${d.adjustment_code}</div>
            <div class="sa-info"><b>Branch:</b> ${d.branch_id}</div>
            <div class="sa-info"><b>Reason:</b> ${d.reason}</div>
            <div class="sa-info"><b>Date:</b> ${new Date(d.created_at).toLocaleDateString("id-ID")}</div>
            <table class="sa-tg">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Type</th>
                  <th>Qty</th>
                </tr>
              </thead>
              <tbody>
                ${d.items
                  .map(
                    (item: any) => `
                  <tr>
                    <td>${item.product.name}</td>
                    <td><span class="${
                      item.type === "IN" ? "badge-in" : "badge-out"
                    }">${item.type}</span></td>
                    <td>${item.qty}</td>
                  </tr>`
                  )
                  .join("")}
              </tbody>
            </table>
          `,
        });
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const onSubmit: SubmitHandler<IInputsStockAdjustment> = (formData: any) => {
    const items = formData.items.map((item: any) => ({
      product_id: item.product_id?.value ?? item.product_id,
      type: item.type,
      qty: parseInt(item.qty),
    }));

    if (editId) {
      axios
        .put(
          (API.STOCK_ADJUSTMENT) + `/${editId}`,
          { reason: formData.reason, items },
          {
            headers: { Authorization: `Bearer ${globalStateUser.token}` },
          }
        )
        .then(() => {
          Swal.fire({
            position: "center",
            icon: "success",
            title: "Stock Adjustment berhasil diupdate!",
            showConfirmButton: false,
            timer: 1500,
          });
          location.reload();
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      axios
        .post(
          API.STOCK_ADJUSTMENT,
          { branch_id: formData.branch_id, reason: formData.reason, items },
          {
            headers: { Authorization: `Bearer ${globalStateUser.token}` },
          }
        )
        .then(() => {
          Swal.fire({
            position: "center",
            icon: "success",
            title: "Stock Adjustment berhasil ditambahkan!",
            showConfirmButton: false,
            timer: 1500,
          });
          location.reload();
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const fetchDataProductList = (inputValue: any, callback: any) => {
    if (!inputValue) {
      callback([]);
    } else {
      setTimeout(() => {
        fetch(
          (API.PRODUCT_LIST) +
            `?filter[name]=${inputValue}`,
          {
            headers: { Authorization: `Bearer ${globalStateUser.token}` },
          }
        )
          .then((res) => res.json())
          .then((res) => {
            const tempArray: any = [];
            res.data.forEach((element: any) => {
              tempArray.push({ label: element.name, value: element.id });
            });
            callback(tempArray);
          })
          .catch((error) => {
            console.log(error);
          });
      });
    }
  };

  return (
    <>
      <div className="text-2xl text-center font-bold mb-4">
        Stock Adjustment
      </div>

      <button
        className="btn btn-primary"
        onClick={() => toggleModal({})}
      >
        Add Stock Adjustment
      </button>

      <Modal
        isOpen={isOpenModal}
        onRequestClose={toggleModal}
        contentLabel="Stock Adjustment"
        className="mymodal w-11/12 lg:w-6/12 lg:h-6/6 overflow-y-auto max-h-full"
        overlayClassName="myoverlay"
        closeTimeoutMS={500}
        shouldCloseOnEsc={false}
      >
        <div>
          <label
            className="btn btn-sm btn-circle absolute right-2 top-2"
            onClick={toggleModal}
          >
            ✕
          </label>
          <h3 className="font-bold text-xl mb-3">
            {editId ? "Edit Stock Adjustment" : "Add Stock Adjustment"}
          </h3>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Branch — shown on create as editable select, on edit as disabled */}
            <label className="label">
              <span className="label-text">Branch</span>
            </label>
            <select
              disabled={!!editId}
              className={`select select-bordered w-full ${
                !!editId ? "bg-gray-100" : ""
              }`}
              {...register("branch_id", { required: !editId })}
            >
              <option value="">Select Branch</option>
              {locationOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.branch_id && (
              <span className="text-sm text-red-700">Field is required!</span>
            )}

            <label className="label">
              <span className="label-text">Reason</span>
            </label>
            <input
              type="text"
              {...register("reason", { required: true })}
              placeholder="Reason..."
              className="border border-slate-300 rounded-md p-[5px] w-full"
            />
            {errors.reason && (
              <span className="text-sm text-red-700">Field is required!</span>
            )}

            {/* Dynamic items */}
            {(fields as any[]).map((field, index) => (
              <div key={field.id}>
                <div className="divider" />
                <div className="flex justify-between items-center mb-1">
                  <p className="font-bold text-sm">Item {index + 1}</p>
                  {index > 0 && (
                    <button
                      type="button"
                      className="btn btn-xs btn-error btn-outline"
                      onClick={() => (remove as any)(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Product */}
                  <div className="basis-1/2">
                    <label className="label">
                      <span className="label-text">Product</span>
                    </label>
                    <Controller
                      control={control}
                      name={`items.${index}.product_id` as any}
                      rules={{ required: true }}
                      render={({ field: { onChange, value } }) => (
                        <AsyncSelect
                          noOptionsMessage={() => "Type to search a product"}
                          value={value}
                          loadOptions={fetchDataProductList}
                          placeholder="Product"
                          onChange={(e: any) => onChange(e)}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          }}
                        />
                      )}
                    />
                    {errors.items && (
                      <span className="text-sm text-red-700">
                        Field is required!
                      </span>
                    )}
                  </div>

                  {/* Type */}
                  <div className="basis-1/4">
                    <label className="label">
                      <span className="label-text">Type</span>
                    </label>
                    <select
                      className="select select-bordered w-full"
                      {...register(`items.${index}.type`, { required: true })}
                    >
                      <option value="IN">IN</option>
                      <option value="OUT">OUT</option>
                    </select>
                  </div>

                  {/* Qty */}
                  <div className="basis-1/4">
                    <label className="label">
                      <span className="label-text">Qty</span>
                    </label>
                    <input
                      type="number"
                      {...register(`items.${index}.qty`, {
                        required: true,
                        min: 1,
                      })}
                      placeholder="0"
                      className="border border-slate-300 rounded-md p-[5px] w-full"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="btn btn-sm btn-outline mt-4"
              onClick={() =>
                (append as any)({ product_id: "", type: "OUT", qty: 0 })
              }
            >
              + Add Item
            </button>

            <button className="btn btn-primary mt-4 w-full" type="submit">
              {editId ? "Update" : "Save"}
            </button>
          </form>
        </div>
      </Modal>

      <div className="mt-6">
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table table-compact w-full">
                <thead>
                  <tr>
                    <th />
                    <th>Adjustment Code</th>
                    <th>Branch</th>
                    <th>Reason</th>
                    <th className="text-center">Date</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item: any, idx: any) => (
                    <tr key={idx}>
                      <th />
                      <td>{item.adjustment_code}</td>
                      <td>{item.branch_id}</td>
                      <td>{item.reason}</td>
                      <td className="text-center">
                        {new Date(item.created_at).toLocaleDateString("id-ID")}
                      </td>
                      <td className="space-x-2 text-center">
                        <button
                          className="btn btn-info btn-sm"
                          onClick={() => handleDetail(item.id)}
                        >
                          Detail
                        </button>
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={() => toggleModal(item)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col sm:flex-row justify-between items-center mt-2">
              <div className="mb-2 sm:mb-0">
                <p>
                  Showing {totalData.from} to {totalData.to} of{" "}
                  {totalData.total} entries
                </p>
              </div>
              <div className="btn-group">
                <button
                  className={
                    !pageNav.preffPage
                      ? `btn btn-sm btn-disabled`
                      : `btn btn-sm bg-gray-700 border-0`
                  }
                  onClick={() => setPage(page - 1)}
                >
                  «
                </button>
                <button className="btn btn-sm bg-gray-700 border-0">
                  Page {page}
                </button>
                <button
                  className={
                    !pageNav.nextPage
                      ? `btn btn-sm btn-disabled`
                      : `btn btn-sm bg-gray-700 border-0`
                  }
                  onClick={() => setPage(page + 1)}
                >
                  »
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
